import { useEffect, useRef, useState, type DragEvent, type ChangeEvent } from "react";
import { useAgentBridge } from "../app/AgentBridge";
import { ACCEPT_ATTR, fileExt, fileToMarkdown } from "./fileToMarkdown";

type ImportMode = "bafa" | "inqa" | "qm_handbuch";

const MODE_LABELS: Record<ImportMode, string> = {
  bafa: "BAFA Beratungsbericht",
  inqa: "INQA Projektreferenz",
  qm_handbuch: "QM Handbuch",
};

interface ImportFile {
  id: number;
  file: File;
  status: "queued" | "converting" | "done" | "error";
  message: string;
  markdown?: string;
}

interface Props {
  mode: ImportMode;
  /** Called when the user wants to leave the import step without applying a result. */
  onSkip: () => void;
  /** Optional headline/description override (e.g. inside a wizard step). */
  title?: string;
  description?: string;
}

let fileCounter = 0;

/**
 * "Daten importieren" form. Sends `import_prefill` over the WebSocket and
 * surfaces `import_prefill_status` / `import_prefill_error` inline. The
 * `import_prefill_result` payload is consumed by the parent wizard, which
 * applies the prefill and advances to its first input step.
 */
export function ImportView({ mode, onSkip, title, description }: Props) {
  const { send, subscribe } = useAgentBridge();
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [text, setText] = useState("");
  const [files, setFiles] = useState<ImportFile[]>([]);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Listen for progress / error frames coming back from the agent. The
  // `import_prefill_result` itself is consumed by the parent wizard (which
  // switches to its first input step and applies the prefill).
  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type === "import_prefill_status") {
        setStatusText(msg.message ?? "");
      } else if (msg.type === "import_prefill_error") {
        setSubmitting(false);
        setStatusText("Fehler: " + (msg.message ?? "Unbekannter Fehler"));
      }
    });
  }, [subscribe]);

  function addFiles(list: FileList | File[]) {
    const additions: ImportFile[] = [];
    for (const file of Array.from(list)) {
      const ext = fileExt(file.name);
      if (!ACCEPT_ATTR.split(",").some((a) => a === "." + ext)) continue;
      additions.push({ id: ++fileCounter, file, status: "queued", message: "" });
    }
    if (additions.length === 0) return;
    setFiles((prev) => [...prev, ...additions]);
  }

  function onFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  }

  function removeFile(id: number) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  async function convertAll(current: ImportFile[]): Promise<ImportFile[]> {
    const results = await Promise.all(
      current.map(async (item) => {
        if (item.status === "done") return item;
        const next: ImportFile = { ...item, status: "converting", message: "Konvertiert…" };
        setFiles((prev) => prev.map((p) => (p.id === item.id ? next : p)));
        try {
          const md = await fileToMarkdown(item.file);
          const done: ImportFile = {
            ...item,
            status: "done",
            message: "Fertig",
            markdown: md.trim() || "(kein Text extrahiert)",
          };
          setFiles((prev) => prev.map((p) => (p.id === item.id ? done : p)));
          return done;
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : String(err);
          const failed: ImportFile = { ...item, status: "error", message: "Fehler: " + errMsg };
          setFiles((prev) => prev.map((p) => (p.id === item.id ? failed : p)));
          return failed;
        }
      }),
    );
    return results;
  }

  async function onSubmit() {
    setSubmitting(true);
    setStatusText("Dateien werden konvertiert…");

    const converted = await convertAll(files);
    const successful = converted.filter((f) => f.status === "done" && f.markdown);
    const failed = converted.filter((f) => f.status === "error");
    const trimmedText = text.trim();
    const trimmedUrl = websiteUrl.trim();

    if (!trimmedText && successful.length === 0 && !trimmedUrl) {
      setStatusText(
        failed.length > 0
          ? "Alle Dateien konnten nicht konvertiert werden. Bitte die Fehler prüfen oder Text eingeben."
          : "Bitte Text eingeben, eine Website-URL angeben oder Dateien hinzufügen.",
      );
      setSubmitting(false);
      return;
    }

    if (failed.length > 0) {
      const failedList = failed.map((f) => `• ${f.file.name}: ${f.message}`).join("\n");
      const totalCount = converted.length;
      const proceed = window.confirm(
        `${failed.length} von ${totalCount} ${totalCount === 1 ? "Datei konnte" : "Dateien konnten"} nicht konvertiert werden:\n\n` +
          failedList +
          (successful.length > 0
            ? `\n\nMit ${successful.length} erfolgreich konvertierter ${successful.length === 1 ? "Datei" : "Dateien"} fortfahren?`
            : "\n\nNur mit dem eingegebenen Text fortfahren?"),
      );
      if (!proceed) {
        setSubmitting(false);
        return;
      }
    }

    const parts: string[] = [];
    if (trimmedUrl) parts.push("## Website-URL\n\n" + trimmedUrl);
    if (trimmedText) parts.push("## Texteingabe\n\n" + trimmedText);
    successful.forEach((f) => {
      parts.push("## Dokument: " + f.file.name + "\n\n" + (f.markdown ?? "(kein Text extrahiert)"));
    });
    if (failed.length > 0) {
      parts.push(
        "## Hinweis: Nicht konvertierbare Dateien\n\n" +
          "Folgende Dateien konnten nicht verarbeitet werden und fehlen in der Analyse:\n" +
          failed.map((f) => `- ${f.file.name} (${f.message})`).join("\n"),
      );
    }
    const combinedContent = parts.join("\n\n---\n\n");

    setStatusText("Der Assistent analysiert die Daten und füllt die Felder aus…");
    const sent = send({ type: "import_prefill", mode, content: combinedContent, websiteUrl: trimmedUrl });
    if (!sent) {
      setSubmitting(false);
      setStatusText("Keine WebSocket-Verbindung. Bitte Seite neu laden.");
    }
  }

  function onAbort() {
    if (submitting) {
      send({ type: "cancel" });
      setSubmitting(false);
      setStatusText(null);
    }
  }

  const heading = title ?? `Daten importieren — ${MODE_LABELS[mode]}`;
  const subline =
    description ??
    "Fügen Sie alle verfügbaren Informationen ein — Text, Dokumente, Audioaufnahmen. Der Assistent füllt die Formularfelder automatisch aus.";

  return (
    <section className="import-view wizard-section">
      <h2 className="form-title import-view-title">{heading}</h2>
      <p className="form-description">{subline}</p>

      <div className="form-row">
        <label htmlFor="import-website-url">
          Website-URL <span className="field-hint">(optional)</span>
        </label>
        <input
          id="import-website-url"
          type="url"
          placeholder="https://…"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="form-row">
        <label htmlFor="import-text">Text / Notizen</label>
        <textarea
          id="import-text"
          rows={6}
          placeholder="Alle verfügbaren Informationen hier einfügen: Notizen, Gesprächsprotokolle, Firmenbeschreibungen, Problemstellungen, Website-Texte…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="form-row">
        <label>Dokumente &amp; Dateien</label>
        <div
          className={`import-dropzone${dragOver ? " dragover" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <p>Dateien hierher ziehen oder klicken zum Auswählen</p>
          <p className="import-dropzone-hint">PDF, DOCX, TXT, MP3, WAV, M4A, OGG, WebM, FLAC, PNG, JPG, WebP</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            hidden
            onChange={onFileInputChange}
          />
        </div>
        {files.length > 0 && (
          <ul className="import-file-list" aria-live="polite">
            {files.map((f) => (
              <li key={f.id} className={`import-file file-${f.status}`}>
                <span className="file-name-import">{f.file.name}</span>
                <span className="file-status-import">{f.message}</span>
                {!submitting && (
                  <button
                    type="button"
                    className="remove-import-file"
                    onClick={() => removeFile(f.id)}
                    title="Entfernen"
                    aria-label={`Datei ${f.file.name} entfernen`}
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {statusText && <p className="import-status">{statusText}</p>}

      <div className="form-actions import-view-actions">
        {submitting ? (
          <button type="button" className="btn btn-secondary" onClick={onAbort}>
            Abbrechen
          </button>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={onSkip}>
            Schritt überspringen
          </button>
        )}
        <button type="button" className="btn btn-primary" onClick={() => void onSubmit()} disabled={submitting}>
          {submitting ? "Daten werden analysiert…" : "Importieren & Ausfüllen"}
        </button>
      </div>
    </section>
  );
}
